import { api } from './api'
import {
  Referral,
  ReferralFormData,
  ReferralFilters,
  ReferralOverview,
} from '../types/referral'

export const referralService = {
  /**
   * Get all referrals with optional filters
   */
  getAll: async (filters?: ReferralFilters): Promise<Referral[]> => {
    const params: Record<string, string> = {}
    if (filters?.patientId) params.patientId = filters.patientId
    if (filters?.referringProviderId) params.referringProviderId = filters.referringProviderId
    if (filters?.status) {
      if (Array.isArray(filters.status)) {
        params.status = filters.status.join(',')
      } else {
        params.status = filters.status
      }
    }
    if (filters?.startDate) params.startDate = filters.startDate
    if (filters?.endDate) params.endDate = filters.endDate
    if (filters?.priority) params.priority = filters.priority

    const response = await api.get<Referral[]>('/referrals', { params })
    return response.data
  },

  /**
   * Get single referral by ID
   */
  getById: async (id: string): Promise<Referral> => {
    const response = await api.get<Referral>(`/referrals/${id}`)
    return response.data
  },

  /**
   * Create new referral
   */
  create: async (data: ReferralFormData): Promise<Referral> => {
    const response = await api.post<Referral>('/referrals', data)
    return response.data
  },

  /**
   * Update existing referral
   */
  update: async (id: string, data: Partial<ReferralFormData>): Promise<Referral> => {
    const response = await api.put<Referral>(`/referrals/${id}`, data)
    return response.data
  },

  /**
   * Cancel referral (soft delete)
   */
  delete: async (id: string): Promise<Referral> => {
    const response = await api.delete<Referral>(`/referrals/${id}`)
    if (!response || !response.data) {
      throw new Error('Failed to delete referral')
    }
    return response.data
  },

  /**
   * Send referral (update status to sent)
   */
  send: async (
    id: string,
    data?: {
      communicationMethod?: string
      healthmailMessageId?: string
      errorMessage?: string
    }
  ): Promise<Referral> => {
    const response = await api.post<Referral>(`/referrals/${id}/send`, data || {})
    return response.data
  },

  /**
   * Get referral overview statistics
   */
  getOverview: async (referringProviderId?: string): Promise<ReferralOverview> => {
    const params: Record<string, string> = {}
    if (referringProviderId) params.referringProviderId = referringProviderId

    const response = await api.get<ReferralOverview>('/referrals/overview', { params })
    return response.data
  },
}
